import { forwardRef, useState, useMemo, useCallback, useRef, useEffect, useImperativeHandle, useLayoutEffect } from "react";
import { Box, Typography, Stack, Grid, styled, css, Popover } from "@mui/material";
import { background, button, divider, text, blue } from "@/theme/colors";
import { rounded, elevation } from "@/theme/variables";
import { typography } from "@/theme/typography";
import { Checkbox } from "@/components/elements/Checkbox";
import { Button } from "@/components/elements/Button";
import { IconBox } from "@/components/elements/IconBox";
import { IconClose, IconExpandMore, IconCheck } from "@/assets/icons";
import { Input } from "@/components/elements/Input";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { truncateWithEllipsis } from "@/utils/string";
import { sortTalentsByRecommended } from "@/utils/talent";

export type TalentOption = {
  id: number;
  fullName: string;
  nickname?: string;
  isRecommendedTalent?: boolean;
};

export type TalentSelectDropdownProps = {
  talents: TalentOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  showConfirmedBelow?: boolean;
  savedIds?: number[];
  /** Fired when internal `tempSelectedIds` diverges from committed `selectedIds` — lets parent gate save on pending picks. */
  onUnsavedChange?: (hasUnsaved: boolean) => void;
};

/** Imperative API exposed via ref — parent can commit pending picks before save. */
export type TalentSelectDropdownRef = {
  /** Commit current `tempSelectedIds` to parent via `onChange`. No-op if there are no unsaved changes. */
  commitPending: () => void;
};

const MAX_VISIBLE_TAGS = 6;
const TAG_FIXED_WIDTH = 145;
const PANEL_OFFSET = 4;
const VIEWPORT_MARGIN = 16;

export const TalentSelectDropdown = forwardRef<TalentSelectDropdownRef, TalentSelectDropdownProps>(({
  talents,
  selectedIds,
  onChange,
  placeholder = "選択してください",
  error = false,
  disabled = false,
  showConfirmedBelow = false,
  savedIds,
  onUnsavedChange,
}, ref) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSelectedIds, setTempSelectedIds] = useState<number[]>(selectedIds);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [overflowAnchorEl, setOverflowAnchorEl] = useState<HTMLElement | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState<React.CSSProperties | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectFieldRef = useRef<HTMLDivElement>(null);
  const confirmedTagsRef = useRef<HTMLDivElement>(null);
  const checkboxListRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const open = Boolean(anchorEl);

  const hasUnsavedChanges = useMemo(() => {
    if (tempSelectedIds.length !== selectedIds.length) return true;
    const sortedTemp = [...tempSelectedIds].sort((a, b) => a - b);
    const sortedSelected = [...selectedIds].sort((a, b) => a - b);
    return sortedTemp.some((id, index) => id !== sortedSelected[index]);
  }, [tempSelectedIds, selectedIds]);

  // Propagate unsaved-state upward so the parent can gate save actions on pending picks.
  useEffect(() => {
    onUnsavedChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChange]);

  // Expose imperative commit so the parent can flush pending picks into `selectedIds` right before saving.
  useImperativeHandle(
    ref,
    () => ({
      commitPending: () => {
        if (hasUnsavedChanges) onChange(tempSelectedIds);
      },
    }),
    [hasUnsavedChanges, onChange, tempSelectedIds],
  );

  const filteredTalents = useMemo(() => {
    let filtered = talents;
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = talents.filter((t) => t.fullName.toLowerCase().includes(query) || (t.nickname && t.nickname.toLowerCase().includes(query)));
    }
    return sortTalentsByRecommended(filtered);
  }, [talents, searchQuery]);

  useEffect(() => {
    if (open) {
      setTempSelectedIds(selectedIds);
      setSearchQuery("");
    }
  }, [open, selectedIds]);

  // The panel stays INLINE in the React tree (rendering the checkbox list through a portal breaks
  // its keyed reconciliation here) but is positioned `fixed` from the select field's rect so it can
  // escape the modal's overflow clipping. Positioned like MUI Select menus: opens below the field,
  // slides up just enough when that would overflow the viewport, and only shrinks (the checkbox
  // list is the part that shrinks) when the viewport itself is too short.
  const updatePanelPosition = useCallback(() => {
    const field = selectFieldRef.current;
    if (!field) return;
    const rect = field.getBoundingClientRect();
    const panel = panelRef.current;
    // Apply the width before measuring: the list wraps by width, so the height depends on it.
    if (panel) panel.style.width = `${rect.width}px`;
    const panelHeight = panel?.offsetHeight ?? 0;
    const top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.bottom + PANEL_OFFSET, window.innerHeight - VIEWPORT_MARGIN - panelHeight),
    );
    setPanelPosition({
      left: rect.left,
      width: rect.width,
      top,
      maxHeight: window.innerHeight - VIEWPORT_MARGIN * 2,
    });
  }, []);

  // Re-position on window resize and whenever the field (tag rows wrap) or the panel (search filters
  // the list) changes size.
  useLayoutEffect(() => {
    if (!open) {
      setPanelPosition(null);
      return;
    }
    updatePanelPosition();
    const observer = new ResizeObserver(updatePanelPosition);
    if (selectFieldRef.current) observer.observe(selectFieldRef.current);
    if (panelRef.current) observer.observe(panelRef.current);
    window.addEventListener("resize", updatePanelPosition);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePanelPosition);
    };
  }, [open, updatePanelPosition]);

  // Lock scrolling while open, like the other Select dropdowns: only the checkbox list may scroll,
  // and hitting its top/bottom must not chain the scroll to the modal behind.
  useEffect(() => {
    if (!open) return;
    const blockScroll = (event: WheelEvent) => {
      const list = checkboxListRef.current;
      const canScrollList =
        !!list &&
        list.contains(event.target as Node) &&
        (event.deltaY < 0 ? list.scrollTop > 0 : list.scrollTop + list.clientHeight < list.scrollHeight);
      if (!canScrollList) event.preventDefault();
    };
    document.addEventListener("wheel", blockScroll, { passive: false });
    return () => document.removeEventListener("wheel", blockScroll);
  }, [open]);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    if (open) {
      handleAttemptClose();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery("");
  };

  const handleAttemptClose = () => {
    if (hasUnsavedChanges) {
      setIsConfirmOpen(true);
    } else {
      handleClose();
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setIsConfirmOpen(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmLeave = () => {
    setTempSelectedIds(selectedIds);
    setIsConfirmOpen(false);
    handleClose();
  };

  const handleCancelLeave = () => {
    setIsConfirmOpen(false);
  };

  const handleConfirm = () => {
    onChange(tempSelectedIds);
    handleClose();
  };

  const handleToggleTalent = useCallback((talentId: number, checked: boolean) => {
    setTempSelectedIds((prev) => {
      if (checked) {
        return prev.includes(talentId) ? prev : [...prev, talentId];
      }
      return prev.filter((id) => id !== talentId);
    });
  }, []);

  const hasNewSelections = useMemo(() => {
    return tempSelectedIds.some((id) => !selectedIds.includes(id));
  }, [tempSelectedIds, selectedIds]);

  const handleClearRequest = useCallback(() => {
    if (hasNewSelections) {
      setIsClearConfirmOpen(true);
    }
  }, [hasNewSelections]);

  const handleConfirmClear = useCallback(() => {
    setTempSelectedIds(selectedIds);
    setIsClearConfirmOpen(false);
  }, [selectedIds]);

  const handleCancelClear = useCallback(() => {
    setIsClearConfirmOpen(false);
  }, []);

  const handleSelectAll = useCallback(() => {
    const filteredIds = filteredTalents.map((t) => t.id);
    setTempSelectedIds(filteredIds);
  }, [filteredTalents]);

  const handleRemoveTag = useCallback(
    (talentId: number, e: React.MouseEvent) => {
      e.stopPropagation();
      const newIds = selectedIds.filter((id) => id !== talentId);
      onChange(newIds);
    },
    [selectedIds, onChange],
  );

  const handleRequestRemoveTag = useCallback((talentId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingDeleteId(talentId);
    setIsDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteId !== null) {
      const newIds = selectedIds.filter((id) => id !== pendingDeleteId);
      onChange(newIds);
    }
    setIsDeleteConfirmOpen(false);
    setPendingDeleteId(null);
  }, [pendingDeleteId, selectedIds, onChange]);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteConfirmOpen(false);
    setPendingDeleteId(null);
  }, []);

  const selectedTalents = useMemo(() => {
    const selected = selectedIds.map((id) => talents.find((t) => t.id === id)).filter((t): t is TalentOption => t !== undefined);
    return sortTalentsByRecommended(selected);
  }, [talents, selectedIds]);

  // Talents that are saved in database (for showing below dropdown when showConfirmedBelow is enabled)
  const savedTalents = useMemo(() => {
    if (!savedIds) return selectedTalents;
    const saved = savedIds
      .filter((id) => selectedIds.includes(id))
      .map((id) => talents.find((t) => t.id === id))
      .filter((t): t is TalentOption => t !== undefined);
    return sortTalentsByRecommended(saved);
  }, [talents, savedIds, selectedIds, selectedTalents]);

  // Talents that are selected but not yet saved (for showing inside dropdown field)
  const unsavedTalents = useMemo(() => {
    if (!savedIds) return [];
    const unsaved = selectedIds
      .filter((id) => !savedIds.includes(id))
      .map((id) => talents.find((t) => t.id === id))
      .filter((t): t is TalentOption => t !== undefined);
    return sortTalentsByRecommended(unsaved);
  }, [talents, selectedIds, savedIds]);

  const visibleTags = selectedTalents.slice(0, MAX_VISIBLE_TAGS);
  const overflowTags = selectedTalents.slice(MAX_VISIBLE_TAGS);
  const overflowCount = Math.max(0, selectedTalents.length - MAX_VISIBLE_TAGS);

  // For saved talents shown below dropdown
  const visibleSavedTags = savedTalents.slice(0, MAX_VISIBLE_TAGS);
  const savedOverflowCount = Math.max(0, savedTalents.length - MAX_VISIBLE_TAGS);

  // For unsaved talents shown inside dropdown field
  const visibleUnsavedTags = unsavedTalents.slice(0, MAX_VISIBLE_TAGS);
  const unsavedOverflowCount = Math.max(0, unsavedTalents.length - MAX_VISIBLE_TAGS);

  const tempSelectedTalents = useMemo(() => {
    const selected = tempSelectedIds.map((id) => talents.find((t) => t.id === id)).filter((t): t is TalentOption => t !== undefined);
    return sortTalentsByRecommended(selected);
  }, [talents, tempSelectedIds]);

  const visibleTempTags = tempSelectedTalents.slice(0, MAX_VISIBLE_TAGS);
  const overflowTempTags = tempSelectedTalents.slice(MAX_VISIBLE_TAGS);
  const tempOverflowCount = Math.max(0, tempSelectedTalents.length - MAX_VISIBLE_TAGS);

  const isOverflowPopoverOpen = Boolean(overflowAnchorEl);
  const handleOverflowClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (open) {
        setOverflowAnchorEl(selectFieldRef.current);
      } else {
        setOverflowAnchorEl(confirmedTagsRef.current || containerRef.current);
      }
    },
    [open],
  );
  const handleOverflowClose = useCallback(() => {
    setOverflowAnchorEl(null);
  }, []);

  const handleRemoveFromOverflow = useCallback(
    (talentId: number, e: React.MouseEvent) => {
      e.stopPropagation();
      if (open) {
        setTempSelectedIds((prev) => prev.filter((id) => id !== talentId));
      } else {
        setPendingDeleteId(talentId);
        setIsDeleteConfirmOpen(true);
        handleOverflowClose();
      }
    },
    [open, handleOverflowClose],
  );

  return (
    <DropdownContainer ref={containerRef}>
      {/* Blocks interaction with everything behind the dropdown while it is open, like a standard
          Select backdrop. Clicking it attempts to close (confirm dialog first when there are unsaved picks). */}
      {open && <DropdownBackdrop onMouseDown={handleAttemptClose} />}
      <SelectField
        ref={selectFieldRef}
        onClick={handleToggle}
        error={error}
        disabled={disabled}
        hasValue={selectedIds.length > 0 || (open && tempSelectedIds.length > 0)}
        sx={open ? { position: "relative", zIndex: 1600 } : undefined}
      >
        <TagsContainer>
          {open ? (
            tempSelectedIds.length === 0 ? (
              <PlaceholderText>{placeholder}</PlaceholderText>
            ) : (
              <>
                {visibleTempTags.map((talent) => (
                  <TipTag key={talent.id}>
                    <TagText>{talent.fullName}</TagText>
                    <IconBox
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleTalent(talent.id, false);
                      }}
                      sx={{ p: 0, mt: 0.2 }}
                    >
                      <IconClose width={16} height={16} color={blue[80]} />
                    </IconBox>
                  </TipTag>
                ))}
                {tempOverflowCount > 0 && <OverflowText onClick={handleOverflowClick}>+{tempOverflowCount}</OverflowText>}
              </>
            )
          ) : showConfirmedBelow ? (
            unsavedTalents.length === 0 ? (
              <PlaceholderText>{placeholder}</PlaceholderText>
            ) : (
              <>
                {visibleUnsavedTags.map((talent) => (
                  <TipTag key={talent.id}>
                    <TagText>{talent.fullName}</TagText>
                    <IconBox onClick={(e) => handleRemoveTag(talent.id, e)} sx={{ p: 0, mt: 0.2 }}>
                      <IconClose width={16} height={16} color={blue[80]} />
                    </IconBox>
                  </TipTag>
                ))}
                {unsavedOverflowCount > 0 && <OverflowText onClick={handleOverflowClick}>+{unsavedOverflowCount}</OverflowText>}
              </>
            )
          ) : selectedIds.length === 0 ? (
            <PlaceholderText>{placeholder}</PlaceholderText>
          ) : (
            <>
              {visibleTags.map((talent) => (
                <TipTag key={talent.id}>
                  <TagText>{talent.fullName}</TagText>
                  <IconBox onClick={(e) => handleRemoveTag(talent.id, e)} sx={{ p: 0, mt: 0.2 }}>
                    <IconClose width={16} height={16} color={blue[80]} />
                  </IconBox>
                </TipTag>
              ))}
              {overflowCount > 0 && <OverflowText onClick={handleOverflowClick}>+{overflowCount}</OverflowText>}
            </>
          )}
        </TagsContainer>
        <IconExpandMore
          fontSize={24}
          color={text.primary}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </SelectField>

      {/* Mounted (hidden) before it is positioned so its height can be measured for the first placement. */}
      {open && (
        <DropdownPanel ref={panelRef} style={panelPosition ?? { visibility: "hidden" }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, pb: 0 }}>
            <Box sx={{ flex: 1 }}>
              <Input
                placeholder="人材の絞り込み"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                nonControl
                clearable
                fullWidth
              />
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                type="button"
                variant="text"
                size="sm"
                onClick={(e) => {
                  e.currentTarget.blur();
                  handleClearRequest();
                }}
                disabled={!hasNewSelections}
              >
                <Typography variant="body14Semi">クリア</Typography>
              </Button>
              <Button variant="outline" size="sm" onClick={handleSelectAll}>
                <Typography variant="body14Semi">全てを選択</Typography>
              </Button>
            </Stack>
          </Stack>

          <CheckboxContainer ref={checkboxListRef}>
            <Grid container spacing={1.5}>
              {filteredTalents.map((talent) => {
                const isSelected = tempSelectedIds.includes(talent.id);
                const displayName = talent.nickname ? `${talent.fullName}（${talent.nickname}）` : talent.fullName;
                return (
                  <Grid item xs={4} key={talent.id}>
                    <Checkbox
                      label={truncateWithEllipsis(displayName, 25)}
                      checked={isSelected}
                      onChange={(e) => handleToggleTalent(talent.id, e.target.checked)}
                      nonControl
                    />
                  </Grid>
                );
              })}
              {filteredTalents.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body14" color={text.tertiary}>
                    該当する人材がありません
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CheckboxContainer>

          <FooterContainer direction="row" justifyContent="space-between" alignItems="center">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              キャンセル
            </Button>
            <Button variant="primary" size="sm" onClick={handleConfirm} startIcon={<IconCheck color={text.white} />}>
              設定
            </Button>
          </FooterContainer>
        </DropdownPanel>
      )}

      <ConfirmSaveModal open={isConfirmOpen} onClose={handleCancelLeave} onOk={handleConfirmLeave} enableFooter>
        <Typography>選択内容が保存されていませんが、閉じてもよろしいですか？</Typography>
      </ConfirmSaveModal>

      {/* Confirmed selections displayed below dropdown when showConfirmedBelow is enabled - only shows saved talents */}
      {showConfirmedBelow && !open && savedTalents.length > 0 && (
        <ConfirmedTagsContainer ref={confirmedTagsRef}>
          {visibleSavedTags.map((talent) => (
            <ConfirmedTag key={talent.id}>
              <ConfirmedTagText>{talent.fullName}</ConfirmedTagText>
              <IconBox onClick={(e) => handleRequestRemoveTag(talent.id, e)} sx={{ p: 0, mt: 0.2, flexShrink: 0 }}>
                <IconClose width={16} height={16} color={blue[80]} />
              </IconBox>
            </ConfirmedTag>
          ))}
          {savedOverflowCount > 0 && <OverflowText onClick={handleOverflowClick}>+{savedOverflowCount}</OverflowText>}
        </ConfirmedTagsContainer>
      )}

      <ConfirmSaveModal open={isDeleteConfirmOpen} onClose={handleCancelDelete} onOk={handleConfirmDelete} enableFooter>
        <Typography>スケジュールからから人材を削除してもよろしいでしょうか？</Typography>
      </ConfirmSaveModal>

      <ConfirmSaveModal open={isClearConfirmOpen} onClose={handleCancelClear} onOk={handleConfirmClear} enableFooter>
        <Typography>選択した人材が全て解除されますがよろしいですか？</Typography>
      </ConfirmSaveModal>

      <Popover
        open={isOverflowPopoverOpen}
        anchorEl={overflowAnchorEl}
        onClose={handleOverflowClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          zIndex: 1600,
          marginTop: -0.5,
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              boxShadow: elevation.lv2,
              border: `1px solid ${divider.middle}`,
              borderRadius: rounded.xs,
              maxHeight: 400,
              overflow: "auto",
              width: selectFieldRef.current?.offsetWidth || containerRef.current?.offsetWidth || "auto", // Match width of SelectField
            },
          },
        }}
      >
        <OverflowPopoverContainer>
          <OverflowPopoverTagsGrid>
            {(open ? overflowTempTags : overflowTags).map((talent) => (
              <OverflowPopoverTag key={talent.id}>
                <OverflowPopoverTagText>{talent.fullName}</OverflowPopoverTagText>
                <IconBox onClick={(e) => handleRemoveFromOverflow(talent.id, e)} sx={{ p: 0, mt: 0.2, flexShrink: 0 }}>
                  <IconClose width={16} height={16} color={blue[80]} />
                </IconBox>
              </OverflowPopoverTag>
            ))}
          </OverflowPopoverTagsGrid>
        </OverflowPopoverContainer>
      </Popover>
    </DropdownContainer>
  );
});

TalentSelectDropdown.displayName = "TalentSelectDropdown";

const DropdownContainer = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DropdownBackdrop = styled(Box)`
  position: fixed;
  inset: 0;
  z-index: 1599;
`;

const DropdownPanel = styled(Box)`
  position: fixed;
  z-index: 1600;
  display: flex;
  flex-direction: column;
  background: ${background.white};
  border: 1px solid ${divider.middle};
  border-radius: ${rounded.xs};
  box-shadow: ${elevation.lv2};
  overflow: hidden;
  animation: fadeSlideIn 0.2s ease-out;

  @keyframes fadeSlideIn {
    0% {
      opacity: 0;
      transform: translateY(-8px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SelectField = styled(Box, {
  shouldForwardProp: (prop) => !["error", "disabled", "hasValue"].includes(prop as string),
})<{ error: boolean; disabled: boolean; hasValue: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  background: ${background.white};
  border: 1px solid ${divider.middle};
  border-radius: ${rounded.xs};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  min-height: 40px;
  transition: border-color 0.2s ease;

  ${({ error }) =>
    error &&
    css`
      border-color: ${button.danger};
      background: ${background.error};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      background: ${background.disable};
      opacity: 0.7;
    `}

  &:hover {
    ${({ disabled, error }) =>
      !disabled &&
      !error &&
      css`
        border-color: ${blue[70]};
      `}
  }
`;

const TagsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
  align-items: center;
`;

const TipTag = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0px 8px;
  background: ${background.secondary};
  border: 1px solid ${button.primary};
  border-radius: ${rounded.md};
  width: ${TAG_FIXED_WIDTH}px;
  box-sizing: border-box;
`;

const TagText = styled(Typography)`
  ${css(typography.cap12)};
  color: ${text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const PlaceholderText = styled(Typography)`
  ${css(typography.body14)};
  color: ${text.tertiary};
`;

const OverflowText = styled(Typography)`
  ${css(typography.body14)};
  color: ${text.primary};
  text-decoration: underline;
  cursor: pointer;
`;

const CheckboxContainer = styled(Box)`
  padding: 16px;
  max-height: 160px;
  overflow-y: auto;
`;

const FooterContainer = styled(Stack)`
  padding: 16px;
  border-top: 1px solid ${divider.high};
  border-radius: 0 0 ${rounded.sm} ${rounded.sm};
  background: ${background.white};
`;

const ConfirmedTagsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  max-width: 95%;
  align-items: center;
`;

const ConfirmedTag = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0px 8px;
  background: ${background.secondary};
  border: 1px solid ${button.primary};
  border-radius: ${rounded.md};
  width: ${TAG_FIXED_WIDTH}px;
  box-sizing: border-box;
`;

const ConfirmedTagText = styled(Typography)`
  ${css(typography.cap12)};
  color: ${text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const OverflowPopoverContainer = styled(Box)`
  padding: 6px 8px;
  background: ${background.white};
`;

const OverflowPopoverTagsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, ${TAG_FIXED_WIDTH}px);
  gap: 8px;
`;

const OverflowPopoverTag = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0px 8px;
  background: ${background.secondary};
  border: 1px solid ${button.primary};
  border-radius: ${rounded.md};
  width: ${TAG_FIXED_WIDTH}px;
  box-sizing: border-box;
`;

const OverflowPopoverTagText = styled(Typography)`
  ${css(typography.cap12)};
  color: ${text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;
